import {ChangeDetectorRef, Component, Input} from '@angular/core';
import moment from 'moment';
import {SubscribedComponent} from "../subscribed.component";
import {ShowableFormGroup} from "../shared/ShowableFormGroup";
import {salutations, Searchable, User, UserGender, UserRole} from "../interfaces/user";
import {DirectoryService} from "../shared/directory.service";
import {Seniority} from "../interfaces/seniority";
import {FormBuilder} from "@angular/forms";
import {BSValidators} from "../shared/bsvalidators";
import {Globals} from "../shared/Globals";
import {UsersService} from "../shared/users.service";
import {Router} from "@angular/router";
import {AuthService} from "../shared/auth.service";
import {CountryISO} from "ngx-intl-tel-input";
import {Capacity, CapacityManagerComponent} from "../controls/capacity-manager/capacity-manager.component";
import {NbLayoutScrollService} from "@nebular/theme";
import {DirectoryStatus} from "../interfaces/directory";
import {Question} from "../interfaces/question";
import {QuestionAssignment} from "../interfaces/questionAssignment";
import {Experience} from "../controls/experience-assigner/experience";
import _ from "lodash";
import {SettingsService} from "../shared/settings.service";
import {Department} from "../interfaces/department";

@Component({
    selector: 'bst-new-user',
    templateUrl: './new-user.component.html',
    styleUrls: ['../project/projects.component.scss', './users.component.scss'],
    standalone: false
})
export class NewUserComponent extends SubscribedComponent {

  @Input() user: User;

  public readonly salutations = salutations;

  // Forms
  public forms: {[key: string]: ShowableFormGroup} = {};
  public questionForm: ShowableFormGroup;
  public seniorities: Seniority[];
  public departments: Department[];
  public questions: Question[];
  public UserRole = UserRole;
  public UserGender = UserGender;
  public CountryISO = CountryISO;
  public Globals = Globals;
  public checking: boolean = false;
  public already: number = 0;

  constructor(private directoryService: DirectoryService,
              private userService: UsersService,
              private router: Router,
              public auth: AuthService,
              public scroll: NbLayoutScrollService,
              private detector: ChangeDetectorRef,
              private settings: SettingsService,
              private fb: FormBuilder) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.mapUser();
  }

  private async mapUser(){
    this.loading.next(true);
    this.forms = {};

    if(!this.user) {
      this.seniorities = await this.directoryService.getSeniority().catch(() => null);
      this.departments = await this.directoryService.getDepartments().catch(() => null);
    }
    else{
      if(this.user.user_to_client.freelancer) {
        this.questions = await this.directoryService.getQuestions(DirectoryStatus.ACTIVE).catch(() => null);

        if (this.questions && this.questions.length) {
          const group = {};
          this.questions.forEach(q => {
            const val = [];
            if (q.definition.required) {
              val.push(BSValidators.required);
            }
            const myanswer = this.user.user_to_client?.questions ? this.user.user_to_client.questions.find(o => o.Question_id === q.id) : null;
            group[q.id] = [myanswer?.answer?.data, val];
          });

          this.questionForm = new ShowableFormGroup(this.fb.group(group))
          // this.forms.questions = new ShowableFormGroup(this.fb.group(group));
        }
      }

      this.forms.superpowers = new ShowableFormGroup(this.fb.group({
        industries: [this.user ? this.user.industries.map(o => o.industry) : null, []],
        categories: [this.user ? this.user.categories : null, []],
        degrees: [this.user ? this.user.degrees.map(o => o.degree) : null, []],
        markets: [this.user ? this.user.markets.map(o => o.market) : null, []],
        languages: [this.user ? this.user.languages.map(o => o.language) : null, []],
        developments: [this.user ? this.user.developments.map(o => o.development) : null, []],
        certifications: [this.user ? this.user.certifications.map(o => o.certification) : null, []]
      }));

      this.forms.career = new ShowableFormGroup(this.fb.group({
        career: [this.user ? this.user.career : null, []]
      }));
    }

    const cap: Capacity = this.user ? this.user.capacity : CapacityManagerComponent.default();

    this.forms.details = new ShowableFormGroup(this.fb.group({
      username: [{value: this.user ? this.user.username : null, disabled: this.user}, [BSValidators.email, BSValidators.required]],
      name_first: [this.user ? this.user.name_first : null, [BSValidators.required]],
      name_last: [this.user ? this.user.name_last : null, [BSValidators.required]],
      salutation: [this.user ? this.user.salutation : null, []],
      // gender: [this.user ? this.user.gender : null, [BSValidators.required, BSValidators.nextValidate(UserGender.UNKNOWN, {gender_other: [BSValidators.required]}, this.detector)]],
      // gender_other: [this.user ? this.user.gender_other : null],
      phone: [this.user ? this.user.phone : null, [BSValidators.phoneValid]],
      birthdate: [this.user ? Globals.fromDateStringToMoment(this.user.birthdate) : null, []],
      location_home: [this.user ? this.user.location_home : null, []]
    }));
    this.subs.push(BSValidators.armPhoneValidator(this.forms.details.form.get('phone')));

    if(!this.user) {
      let a: Promise<any> = Promise.resolve(null);
      const de = _.debounce(() => {
        a = a.then(() => {
          const form = this.forms.details.form;
          const keys = ['name_first', 'name_last', 'salutation'];
          const clear = () => {
            this.already = 0;
            keys.forEach((k) => {
              if(form.get(k).disabled) {
                form.get(k).setValue(null);
                form.get(k).enable();
              }
            });
          };
          const email = form.get('username').value;
          if (email) {
            this.checking = true;
            return this.userService.checkUser(email).then((check: any) => {
              if(check){
                if(check.in_client) {
                  form.get('username').setErrors({
                    'alreadyexists': true
                  });
                  this.already = 2;
                }
                else if(check.unavailable) {
                  form.get('username').setErrors({
                    'otherorganisation': true
                  });
                  this.already = 3;
                }
                else{
                  this.already = 1;
                }

                keys.forEach((k) => {
                  form.get(k).setValue(check[k]);
                  form.get(k).disable();
                });
              }
              else{
                clear();
              }
              this.checking = false;
            });
          }
          else{
            clear();
          }
        });
        return a;
      }, 200);
      this.subs.push(this.forms.details.form.get('username').valueChanges.subscribe(de));
    }

    const uoffered = this.settings.userSetting('user_offered', 120);
    const ucost = this.settings.userSetting('user_cost', 60);
    const ucap = {capacity: cap, upcoming_capacity: null};
    const voffered = this.user ? this.user.user_to_client.offered : uoffered;
    this.forms.prof = new ShowableFormGroup(this.fb.group({
      freelancer: [this.user ? this.user.user_to_client?.freelancer : 0, [BSValidators.required,
        BSValidators.nextValidate(0, {
          seniority: [BSValidators.required],
          offered: [BSValidators.required, BSValidators.pattern(/\d/)],
          company: []
        }, this.detector, {
          offered: voffered
        }),
        BSValidators.nextValidate(1, {
          company: [BSValidators.required]
        }, this.detector)
      ]],
      company: [this.user ? this.user.company : null, []],
      start_on: [{value: this.user ? Globals.fromDateStringToMoment(this.user.user_to_client.start_on) ?? moment() : moment(), disabled: this.user && !this.user.user_to_client.freelancer}, [BSValidators.required]],
      end_on: [{value: this.user ? Globals.fromDateStringToMoment(this.user.user_to_client.end_on) : null, disabled: this.user && !this.user.user_to_client.freelancer}, []],
      role: [UserRole.USER],
      seniority: [this.user ? this.user.user_to_client.seniority?.id : null, []],
      department: [this.user ? this.user.user_to_client.department?.id : null, []],
      parent: [this.user ? this.user.user_to_client.parent?.id : null, []],
      offered: [{value: voffered, disabled: this.user && !this.user.user_to_client.freelancer}, []],
      cost: [{value: this.user ? this.user.user_to_client.cost : ucost, disabled: this.user && !this.user.user_to_client.freelancer}, [BSValidators.required, BSValidators.pattern(/\d/)]],
      capacity: [{value: ucap, disabled: this.user && !this.user.user_to_client.freelancer}, [BSValidators.required]],
      cv: [this.cv, this.user ? [BSValidators.required] : []]
    }));

    this.loading.next(false);
  }

  public get cv(){
    return (this.user && this.user.file_entries ? this.user.file_entries.find(o => o.tag === 'cv') : null) || null;
  }

  private static remap(stuff: Searchable[]){
    if(stuff){
      return stuff.map((o) => {
        return o.id;
      });
    }
    return [];
  }

  private acquireUser(): any{
    let newuser:any = this.user ?? {};
    Object.values(this.forms).forEach((o) => {
      newuser = {...newuser, ...o.form.value};
    });

    // newuser.categories = NewUserComponent.remap(newuser.categories);
    newuser.industries = NewUserComponent.remap(newuser.industries);
    newuser.languages = NewUserComponent.remap(newuser.languages);
    newuser.markets = NewUserComponent.remap(newuser.markets);
    newuser.degrees = NewUserComponent.remap(newuser.degrees);
    newuser.developments = NewUserComponent.remap(newuser.developments);
    newuser.certifications = NewUserComponent.remap(newuser.certifications);
    newuser.phone = newuser.phone ? newuser.phone.e164Number : null;
    newuser.parent_id = newuser.parent ? newuser.parent.id : null;

    if(this.user && this.questionForm){
      const answers = this.questionForm.form.getRawValue();
      if(answers){
        newuser.questions = [];
        for(let ans in answers){
          const qu: QuestionAssignment = {
            Question_id: parseInt(ans),
            User_id: this.user.id,
            answer: answers[ans]
          };
          newuser.questions.push(qu);
        }
      }
    }

    return newuser;
  }

  submit(){
    this.loading.next(true);
    const newuser = this.acquireUser();
    // clean up
    if(newuser.start_on){
      newuser.start_on = Globals.toISODate(newuser.start_on);
    }
    if(newuser.end_on){
      newuser.end_on = Globals.toISODate(newuser.end_on);
    }
    if(newuser.birthdate){
      newuser.birthdate = Globals.toISODate(newuser.birthdate);
    }
    if(newuser.career){
      newuser.career.forEach((master: Experience) => {
        master.items.forEach((item: Experience) => {
          if(item.start){
            item.start = Globals.toISODate(item.start);
          }
          if(item.end){
            item.end = Globals.toISODate(item.end);
          }
        });
        if(master.start){
          master.start = Globals.toISODate(master.start);
        }
        if(master.end){
          master.end = Globals.toISODate(master.end);
        }
      });
    }

    console.log(newuser);
    const p: Promise<User> = (this.user ? this.userService.saveUser(this.user.id, <User>newuser) : this.userService.addUser(<User>newuser));
    return p.then((user: User) => {
      if(this.user){
        return this.auth.bootstrap().then(() => {
          this.loading.next(false);
          return this.router.navigate(['users/me']);
        });
      }
      else{
        this.loading.next(false);
        return this.router.navigate(['users/' + user.id]);
      }


    }).catch(error => {
      console.error(error);
      this.loading.next(false);
    });
  }

  get isfreelancer(){
    return !((!this.user && !this.forms.prof.form.get('freelancer').value) || (this.user && !this.user.user_to_client?.freelancer));
  }

  sendInvite() {
    this.loading.next(true);
    const newuser = this.acquireUser();
    return this.userService.inviteNewUser(newuser).then((did) => {
      this.loading.next(false);
      if(did) {
        return this.router.navigate(['users/view/invited']);
      }
      console.error('did not invite user');
    }).catch(() => {
      this.loading.next(false);
    });
  }
}
